import { useState, type ReactNode, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

// =============================================================================
// Types
// =============================================================================

interface Offer {
  id: string;
  name: string;
  merchantName: string | null;
  category: string | null;
  niche: string | null;
  commissionType: string;
  commissionValue: number | null;
  overallScore: number | null;
  trendingScore: number | null;
  competitionDensity: number | null;
  profitabilityScore: number | null;
  status: string;
  description: string | null;
  terms: string | null;
  cookieDays: number | null;
}

interface AffiliateProgram {
  id: string;
  name: string;
  slug: string;
  status: string;
}

interface OffersResponse {
  success: boolean;
  data?: {
    offers?: Offer[];
    pagination?: {
      total: number;
      hasMore: boolean;
    };
  };
}

interface OfferResponse {
  success: boolean;
  data?: {
    offer: Offer;
    scoreDetails?: {
      trendingScore: number;
      competitionDensity: number;
      profitabilityScore: number;
      overallScore: number;
    };
  };
}

interface ProgramsResponse {
  success: boolean;
  data?: {
    programs: AffiliateProgram[];
  };
}

interface CreateLinkResponse {
  success: boolean;
  data?: {
    id: string;
    code: string;
  };
  error?: {
    message: string;
  };
}

// =============================================================================
// Offer Details Modal
// =============================================================================

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: string | null;
}

function DetailsModal({ isOpen, onClose, offerId }: DetailsModalProps): ReactNode {
  const { accessToken, tenant } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['offer', offerId],
    queryFn: async (): Promise<OfferResponse> => {
      const res = await fetch(`${API_URL}/offers/${offerId ?? ''}?includeScoreDetails=true`, {
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
          ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch offer');
      return (await res.json()) as OfferResponse;
    },
    enabled: isOpen && offerId !== null,
  });

  if (!isOpen) return null;

  const offer = data?.data?.offer;
  const scores = data?.data?.scoreDetails;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Offer Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ) : offer ? (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{offer.name}</h3>
                <p className="text-sm text-gray-500">{offer.merchantName ?? 'Unknown merchant'}</p>
              </div>

              {offer.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                  <p className="text-sm text-gray-600">{offer.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Commission</h4>
                  <p className="text-lg font-semibold text-primary-600">
                    {offer.commissionType === 'percentage'
                      ? `${String(offer.commissionValue ?? 0)}%`
                      : `$${String(offer.commissionValue ?? 0)}`}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Cookie Duration</h4>
                  <p className="text-lg font-semibold text-gray-900">{offer.cookieDays ?? 30} days</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Category</h4>
                  <p className="text-sm text-gray-900">{offer.category ?? 'Uncategorized'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Niche</h4>
                  <p className="text-sm text-gray-900">{offer.niche ?? 'General'}</p>
                </div>
              </div>

              {scores && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">AI Scores</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <ScoreBar label="Overall" value={scores.overallScore} />
                    <ScoreBar label="Trending" value={scores.trendingScore} />
                    <ScoreBar label="Profitability" value={scores.profitabilityScore} />
                    <ScoreBar label="Competition" value={100 - scores.competitionDensity} invertColor />
                  </div>
                </div>
              )}

              {offer.terms && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Terms</h4>
                  <p className="text-xs text-gray-500">{offer.terms}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500">Offer not found</p>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreBar({ label, value, invertColor }: { label: string; value: number; invertColor?: boolean }): ReactNode {
  const percentage = Math.min(100, Math.max(0, value));
  const color = invertColor
    ? percentage >= 60 ? 'bg-green-500' : percentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'
    : percentage >= 80 ? 'bg-green-500' : percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium">{percentage.toFixed(0)}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${String(percentage)}%` }} />
      </div>
    </div>
  );
}

// =============================================================================
// Create Link Modal
// =============================================================================

interface CreateLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: Offer | null;
}

function CreateLinkModal({ isOpen, onClose, offer }: CreateLinkModalProps): ReactNode {
  const { accessToken, tenant } = useAuthStore();
  const queryClient = useQueryClient();

  const [selectedProgram, setSelectedProgram] = useState('');
  const [linkName, setLinkName] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [createdLink, setCreatedLink] = useState<{ code: string } | null>(null);

  // Fetch user's affiliate programs
  const { data: programsData, isLoading: programsLoading } = useQuery({
    queryKey: ['affiliate-programs'],
    queryFn: async (): Promise<ProgramsResponse> => {
      const res = await fetch(`${API_URL}/affiliate/programs?status=active`, {
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
          ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch programs');
      return (await res.json()) as ProgramsResponse;
    },
    enabled: isOpen,
  });

  const createLinkMutation = useMutation({
    mutationFn: async (data: {
      programId: string;
      name: string;
      destinationUrl: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
    }): Promise<CreateLinkResponse> => {
      const res = await fetch(`${API_URL}/affiliate/links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken ?? ''}`,
          ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
        },
        body: JSON.stringify(data),
      });
      return (await res.json()) as CreateLinkResponse;
    },
    onSuccess: (data) => {
      if (data.success && data.data) {
        setCreatedLink({ code: data.data.code });
        void queryClient.invalidateQueries({ queryKey: ['affiliate-links'] });
      }
    },
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!offer || !selectedProgram) return;

    // Build destination URL (this would typically come from the offer's merchant link)
    const destinationUrl = `https://merchant.example.com/offer/${offer.id}`;

    const data: {
      programId: string;
      name: string;
      destinationUrl: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
    } = {
      programId: selectedProgram,
      name: linkName || `${offer.name} Link`,
      destinationUrl,
    };
    if (utmSource) data.utmSource = utmSource;
    if (utmMedium) data.utmMedium = utmMedium;
    if (utmCampaign) data.utmCampaign = utmCampaign;

    createLinkMutation.mutate(data);
  };

  const handleClose = (): void => {
    setSelectedProgram('');
    setLinkName('');
    setUtmSource('');
    setUtmMedium('');
    setUtmCampaign('');
    setCreatedLink(null);
    onClose();
  };

  if (!isOpen) return null;

  const programs = programsData?.data?.programs ?? [];
  const trackingUrl = createdLink ? `https://go.profitos.pro/${createdLink.code}` : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

        <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Create Affiliate Link</h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {createdLink ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <div className="flex items-center gap-2 text-green-800 mb-2">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Link Created!</span>
                </div>
                <div className="bg-white rounded border p-3">
                  <p className="text-xs text-gray-500 mb-1">Your tracking URL:</p>
                  <code className="text-sm text-primary-600 break-all">{trackingUrl}</code>
                </div>
              </div>
              <button
                onClick={() => { void navigator.clipboard.writeText(trackingUrl ?? ''); }}
                className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                Copy to Clipboard
              </button>
              <button
                onClick={handleClose}
                className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-900">{offer?.name}</p>
                <p className="text-xs text-gray-500">{offer?.merchantName}</p>
              </div>

              <div>
                <label htmlFor="program" className="block text-sm font-medium text-gray-700">
                  Select Program
                </label>
                {programsLoading ? (
                  <div className="mt-1 h-10 bg-gray-100 rounded animate-pulse" />
                ) : programs.length === 0 ? (
                  <div className="mt-1 p-3 rounded-md bg-yellow-50 border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      No active programs. <a href="/affiliate/programs" className="underline">Create one first</a>.
                    </p>
                  </div>
                ) : (
                  <select
                    id="program"
                    value={selectedProgram}
                    onChange={(e) => { setSelectedProgram(e.target.value); }}
                    required
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">Select a program...</option>
                    {programs.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label htmlFor="linkName" className="block text-sm font-medium text-gray-700">
                  Link Name (optional)
                </label>
                <input
                  type="text"
                  id="linkName"
                  value={linkName}
                  onChange={(e) => { setLinkName(e.target.value); }}
                  placeholder={offer?.name ?? 'My affiliate link'}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">UTM Parameters (optional)</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label htmlFor="utmSource" className="block text-xs text-gray-500">Source</label>
                    <input
                      type="text"
                      id="utmSource"
                      value={utmSource}
                      onChange={(e) => { setUtmSource(e.target.value); }}
                      placeholder="blog"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="utmMedium" className="block text-xs text-gray-500">Medium</label>
                    <input
                      type="text"
                      id="utmMedium"
                      value={utmMedium}
                      onChange={(e) => { setUtmMedium(e.target.value); }}
                      placeholder="post"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="utmCampaign" className="block text-xs text-gray-500">Campaign</label>
                    <input
                      type="text"
                      id="utmCampaign"
                      value={utmCampaign}
                      onChange={(e) => { setUtmCampaign(e.target.value); }}
                      placeholder="review"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {createLinkMutation.error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3">
                  <p className="text-sm text-red-600">
                    {createLinkMutation.error instanceof Error ? createLinkMutation.error.message : 'Failed to create link'}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLinkMutation.isPending || programs.length === 0}
                  className="flex-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {createLinkMutation.isPending ? 'Creating...' : 'Create Link'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function OffersPage(): ReactNode {
  const { accessToken, tenant } = useAuthStore();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState<'overall' | 'trending' | 'competition' | 'profitability'>('overall');
  const [detailsOfferId, setDetailsOfferId] = useState<string | null>(null);
  const [createLinkOffer, setCreateLinkOffer] = useState<Offer | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['offers', search, category, sortBy],
    queryFn: async (): Promise<OffersResponse> => {
      const params = new URLSearchParams();
      if (search !== '') { params.set('search', search); }
      if (category !== '') { params.set('category', category); }
      params.set('sortBy', sortBy);
      params.set('limit', '20');

      const res = await fetch(`${API_URL}/offers?${String(params)}`, {
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
          ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
        },
      });

      if (!res.ok) { throw new Error('Failed to fetch offers'); }
      return (await res.json()) as OffersResponse;
    },
  });

  const offers: Offer[] = data?.data?.offers ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Offer Intelligence</h2>
        <p className="text-gray-500">
          Discover high-converting affiliate offers with AI-powered scoring
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          placeholder="Search offers..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); }}
          className="flex-1 rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value); }}
          className="rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">All Categories</option>
          <option value="health">Health & Wellness</option>
          <option value="finance">Finance</option>
          <option value="software">Software</option>
          <option value="ecommerce">E-commerce</option>
          <option value="education">Education</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value as typeof sortBy); }}
          className="rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="overall">Sort: Overall Score</option>
          <option value="trending">Sort: Trending</option>
          <option value="competition">Sort: Low Competition</option>
          <option value="profitability">Sort: Profitability</option>
        </select>
      </div>

      {/* Offers Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="animate-pulse rounded-lg border bg-white p-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-600">Failed to load offers. Please try again.</p>
        </div>
      ) : offers.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No offers found</h3>
          <p className="mt-2 text-gray-500">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {offers.map((offer) => (
            <div key={offer.id} className="rounded-lg border bg-white p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 line-clamp-2">{offer.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{offer.merchantName ?? 'Unknown merchant'}</p>
                </div>
                {offer.overallScore !== null && (
                  <div className="ml-2 flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      offer.overallScore >= 80 ? 'bg-green-100 text-green-800' :
                      offer.overallScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {offer.overallScore.toFixed(0)}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="text-gray-500">{offer.category ?? 'Uncategorized'}</span>
                <span className="text-primary-600 font-medium">
                  {offer.commissionType === 'percentage'
                    ? `${String(offer.commissionValue ?? 0)}%`
                    : `$${String(offer.commissionValue ?? 0)}`}
                </span>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => { setCreateLinkOffer(offer); }}
                  className="flex-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Create Link
                </button>
                <button
                  onClick={() => { setDetailsOfferId(offer.id); }}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <DetailsModal
        isOpen={detailsOfferId !== null}
        onClose={() => { setDetailsOfferId(null); }}
        offerId={detailsOfferId}
      />
      <CreateLinkModal
        isOpen={createLinkOffer !== null}
        onClose={() => { setCreateLinkOffer(null); }}
        offer={createLinkOffer}
      />
    </div>
  );
}
